/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Cell, Grid, Link, Theme, useTheme } from 'bold-ui'
import { RouterLink } from 'components/route'
import { useMedicoesAtendimentosAnterioresQuery } from 'graphql/hooks.generated'
import { AtendimentoProfissional } from 'graphql/types.generated'
import {
  getMedicaoForImcReferencia,
  getMedicoesAntropometricasPreNatalSorted,
} from 'view/atendimentos/detail/components/modals/medicoes/pre-natal/util-medicoesPreNatal'

import { AcompanhamentoCard } from '../../components/AcompanhamentoCard'
import {
  convertToUltimasMedicoesRegistradas,
  formatMedicaoString,
  formatMovimentacaoFetalString,
  formatPesoString,
  getMedicoesPreNatal,
} from '../util-acompanhamentoPreNatal'
import { IconLabelPreNatal } from './IconLabelPreNatal'

interface MedicoesGestacaoCardProps {
  prontuarioId: ID
  dataInicioGestacao: Date
  atendimentosProfUltimaGestacao: AtendimentoProfissional[]
  medicoesPath: string
}

export function MedicoesGestacaoCard(props: Readonly<MedicoesGestacaoCardProps>) {
  const { prontuarioId, dataInicioGestacao, atendimentosProfUltimaGestacao, medicoesPath } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const {
    data: { medicoes },
    loading: loadingMedicoes,
  } = useMedicoesAtendimentosAnterioresQuery({
    variables: { prontuarioId },
    fetchPolicy: 'cache-first',
  })

  const medicoesAntropometricasPreNatal = getMedicoesAntropometricasPreNatalSorted(medicoes)
  const medicoesPreNatal = getMedicoesPreNatal(atendimentosProfUltimaGestacao)

  const medicaoForImcReferencia = getMedicaoForImcReferencia(dataInicioGestacao, medicoesAntropometricasPreNatal)

  const ultimasMedicoes = convertToUltimasMedicoesRegistradas(medicoesPreNatal)

  return (
    <AcompanhamentoCard
      id='medicoes-gestante'
      title='Medições da gestante'
      loading={loadingMedicoes}
      placeholder='Nenhuma medição registrada'
      headerContent={
        ultimasMedicoes && (
          <Link
            component={RouterLink}
            to={medicoesPath}
            textDecoration='none'
            fontSize={0.875}
            style={styles.headerLink}
          >
            Visualizar histórico de medições
          </Link>
        )
      }
      justifyHeaderContent='space-between'
    >
      {ultimasMedicoes && (
        <Grid gap={0}>
          <Cell size={4} style={styles.upperItem}>
            <IconLabelPreNatal
              icon='fundalHeightFilled'
              iconText='Altura uterina'
              childrenText={formatMedicaoString(ultimasMedicoes.alturaUterina, 'cm')}
            />
          </Cell>
          <Cell size={4} style={styles.upperItem}>
            <IconLabelPreNatal
              icon='balancePlus'
              iconText='Ganho de peso'
              childrenText={formatPesoString(ultimasMedicoes.peso, medicaoForImcReferencia?.peso)}
            />
          </Cell>
          <Cell size={4} style={styles.upperItem}>
            <IconLabelPreNatal
              icon='bloodPressureFilled'
              iconText='Pressão arterial'
              childrenText={formatMedicaoString(ultimasMedicoes.pressaoArterial, 'mmHg')}
            />
          </Cell>
          <Cell size={12} style={styles.middleItem}>
            <hr css={styles.divider} />
          </Cell>
          <Cell size={4} style={styles.lowerItem}>
            <IconLabelPreNatal
              icon='heartRateFilled'
              iconText='BCF'
              childrenText={formatMedicaoString(ultimasMedicoes.batimentoCardiacoFetal, 'bpm')}
            />
          </Cell>
          <Cell size={4} style={styles.lowerItem}>
            <IconLabelPreNatal
              icon='edema'
              iconText='Edema'
              childrenText={formatMedicaoString(ultimasMedicoes.edema)}
            />
          </Cell>
          <Cell size={4} style={styles.lowerItem}>
            <IconLabelPreNatal
              icon='exchange'
              iconText='Movimentação fetal'
              childrenText={formatMovimentacaoFetalString(ultimasMedicoes.movimentacaoFetal)}
            />
          </Cell>
        </Grid>
      )}
    </AcompanhamentoCard>
  )
}

const createStyles = (theme: Theme) => ({
  headerLink: css`
    line-height: 1.8;
  `,
  upperItem: css`
    padding: 1rem 0.5rem 0;
  `,
  middleItem: css`
    margin: 0;
    padding: 0;
  `,
  lowerItem: css`
    padding: 0 0.5rem 1rem;
  `,
  divider: css`
    margin: 1rem 0.5rem;
    border-bottom: 1px solid ${theme.pallete.divider};
  `,
})
